import axios, { AxiosRequestConfig, Canceler } from "axios"
import { get, isEmpty, trim } from "lodash-es"
import qs from "qs"

import { ApiResult } from "@/api/types"
import { HttpStatusCode } from "@/enums/http-status-code"
import AppBridgeHelper from "@/utils/app-bridge-helper"
import EnvHelper from "@/utils/env-helper"
import StringHelper from "@/utils/string-helper"
import toast from "@/utils/toast"

import { bannerBridge } from "./Banner"
import { ResponseErrorMessage } from "./types/request"

// axios 实例
const goApiUrl = EnvHelper.goApiURL
const request = axios.create({
  baseURL: EnvHelper.apiURL,
  responseType: "json",
  timeout: 12_000,
})

request.defaults.timeout = 12_000

// 后端要求，分页时候page、current_page都需要（虽然他们是同一个值）
export const formatQueryParams = (params: any) => {
  if (!params) return params
  const page = params.page || params.current_page
  // 有page没有current_page

  if (page) {
    params.current_page = page
    params.page = page
  }
  return params
}
export const SERVICE_BANNER_CODE = 1000007
export const SERVICE_SUCCESS_CODE = 1000
export const SERVICE_TOAST_CODE = 40001
/**
 * 之所以加这个方法是因为申请build for shopify时官方要求不能把具体的报错用toast显示
 * 但是有当心后期有需要放开后端的错误提示，所以这里加这个方法
 * 更推荐的做法其实是使用统一的formatRequest函数进行接口包裹，从而统一处理错误信息
 * @param err
 * @returns
 */
export function hideServerErr(err: any) {
  return t("OperationFailed") || err
}

type Code2Msg = { [key in number | "*"]?: string }
// 接口层自定义信息
interface CustomMsgInfo {
  duration?: number
  success?: Code2Msg & { duration?: number }
  error?: Code2Msg & { duration?: number }
  hideMsg?: boolean
  showErrorMsg?: boolean
}
// 大于2000, 不超过3000的 展示错误文本
// 大于3000或1000007的 展示banner错误文本
export async function formatRequest<T = any>(
  promise: any,
  customMsgInfo?: CustomMsgInfo,
): Promise<ApiResult<T>> {
  const res = await promise
  switch (res?.data?.code) {
    case SERVICE_SUCCESS_CODE: {
      const successMsg =
        customMsgInfo?.success?.[res?.data?.code] ||
        customMsgInfo?.success?.["*"]

      if (successMsg)
        toast(
          successMsg,
          customMsgInfo?.success?.duration ?? customMsgInfo?.duration,
        )
      break
    }
    // 如果是这个code，需要显示ErrorBanner的样式，现已经封装好，只需要在错误的地方加上ErrorBanner占位符就可以
    case SERVICE_BANNER_CODE:
      break

    // toast 提示后端信息 （测试服提示、开发人员查看）
    case SERVICE_TOAST_CODE:
      toast(
        res?.data?.message || (res?.data as any)?.msg,
        customMsgInfo?.error?.duration ?? customMsgInfo?.duration,
        true,
      )
      break

    default: {
      // api报错不展示toast提示语
      if (customMsgInfo?.hideMsg) {
        break
      }
      const errorMsg =
        customMsgInfo?.error?.[res?.data?.code] || customMsgInfo?.error?.["*"]
      const errorDuration =
        customMsgInfo?.error?.duration || customMsgInfo?.duration

      if (errorMsg) {
        toast(errorMsg, errorDuration, true)
        break
      }
      if (customMsgInfo?.showErrorMsg) {
        toast(
          res?.data?.message || (res?.data as any)?.msg,
          errorDuration,
          true,
        )
        break
      }
      const msg = res?.data?.message || (res?.data as any)?.msg
      // 大于2000, 不超过3000的 展示错误文本
      // 后面新增了1000012, 1020, 1014的code，需要提示出来
      // 1002错误需要提示出来
      if (
        (res.data.code > 2000 && res.data.code <= 3000) ||
        [1000012, 1020, 1014, 1002].includes(res.data.code)
      ) {
        toast(msg, errorDuration, true)
        break
      }

      toast(t("OperationFailed"), errorDuration, true)
      break
    }
  }

  return res?.data as unknown as ApiResult<T>
}

// 添加请求拦截器
request.interceptors.request.use(
  (request) => {
    removePending(request) // 在请求开始前，对之前的请求做检查取消操作
    addPending(request) // 将当前请求添加到 pending 中

    // 自定义超时时间
    if (request.timeout) {
      request.timeout = request.timeout || 12000
    }

    if (request.headers) {
      // 告诉后端选中了哪个语言
      request.headers["U-Select-Lang"] = "en"

      // 在头部添加 request-id 安装时不添加
      if (request.url !== "/v1/authorization") {
        request.headers["Request-Id"] = StringHelper.generateRandomString(37)
      }

      // 如果是/go-api 开头 则修改baseURL
      if (request.url && request.url.startsWith("/go-api")) {
        request.baseURL = goApiUrl
      }
    }

    return AppBridgeHelper.getSessionToken(request)
  },
  (error) => {
    return Promise.reject(error)
  },
)

// 添加响应拦截器
request.interceptors.response.use(
  (response) => {
    removePending(response.config)
    if (response.config.url) {
      const { url } = response.config
      const { msg } = response.data
      // 全局处理 msg 首字母大写
      if (msg && typeof msg === "string") {
        response.data.msg = msg.charAt(0).toUpperCase() + msg.slice(1)
      }

      if (response.data.code === SERVICE_SUCCESS_CODE) {
        bannerBridge.triggerRemove(url)
      }
      if (response.data.code === SERVICE_BANNER_CODE) {
        bannerBridge.triggerAdd(url, response.data.message || response.data.msg)
      }
    }
    return response
  },
  (error) => {
    // 关闭加载
    if (axios.isCancel(error)) {
      // 中断 Promise 调用链
      return new Promise(() => void 0)
    }

    // 网络层面错误
    if (
      error.code === "ECONNABORTED" ||
      error.message === "Network Error" ||
      error.message.includes("timeout")
    ) {
      error.message = "Network timeout"
    } else {
      // 服务器层面响应错误
      const { response } = error

      if (!response) {
        error.message = "Request error"
        return
      }

      // 结构 API 响应数据
      const {
        status,
        statusText,
        data,
      }: {
        status: number
        [keys: string]: any
      } = response

      const APIResError = get(response, "data.message", null)

      switch (true) {
        case APIResError !== null:
          error.message = APIResError
          break
        case Object.prototype.hasOwnProperty.call(
          ResponseErrorMessage,
          "status",
        ):
          error.message = ResponseErrorMessage[status]
          break

        // 请求参数错误
        case [
          HttpStatusCode.HTTP_BAD_REQUEST.valueOf(),
          HttpStatusCode.HTTP_UNPROCESSABLE_ENTITY,
        ].includes(status):
          error.message = data?.message ?? statusText
          break

        default:
          error.message = "Request error, please try to refresh page."
      }
    }
    // 统一用save failed 调试看接口返回参数
    toast(`${error.message}`, 5e3, true)
    // toast("Save failed", 5e3, true)

    return Promise.reject(error)
  },
)

export default request

// ///////////////////////////////////////////////////////////
// ////////////////////////取消重复请求配置/////////////////////
// ///////////////////////////////////////////////////////////

// 声明一个 Map 用于存储每个请求的标识 和 取消函数
const pending = new Map<string, Canceler>()

/**
 * 添加请求
 * @param {Object} config
 */
const addPending = (config: AxiosRequestConfig) => {
  const id = pendingID(config)

  config.cancelToken =
    config.cancelToken ||
    new axios.CancelToken((cancel) => {
      if (!pending.has(id)) {
        // 如果 pending 中不存在当前请求，则添加进去
        pending.set(id, cancel)
      }
    })
}

/**
 * 移除请求
 * @param {Object} config
 */
const removePending = (config: AxiosRequestConfig) => {
  const id = pendingID(config)

  if (pending.has(id)) {
    // 如果在 pending 中存在当前请求标识，需要取消当前请求，并且移除
    const cancel = pending.get(id)

    cancel && cancel(id)
    pending.delete(id)
  }
}

/**
 * 获取 pending ID
 * @param config
 */
const pendingID = (config: AxiosRequestConfig) => {
  const hash = [config.method, config.url]

  !isEmpty(config.data) && hash.push(qs.stringify(config.data))
  !isEmpty(config.params) && hash.push(qs.stringify(config.params))

  return trim(hash.join("&"), "&/")
}
